import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, Step, StepButton, Stepper, Typography,
} from '@mui/material';
import { ArrowBack, Cancel } from '@mui/icons-material';
import { orderProcessorAPI } from '../../services/api';
import { errMsg, fmtDateTime, StatusChip } from './common';
import ReviewStep from './ReviewStep';
import DistributorStep from './DistributorStep';
import PurchaseOrders from './PurchaseOrders';

interface Props {
  runId: number;
  onBack: () => void;
}

const STEPS = ['Review order items', 'Distributors & strips', 'Purchase orders'];
const stepForStatus = (s: string) => (s === 'draft' ? 0 : s === 'reviewed' || s === 'distributors_assigned' ? 1 : 2);

const RunWorkspace: React.FC<Props> = ({ runId, onBack }) => {
  const [run, setRun] = useState<any | null>(null);
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (moveToStatusStep = false) => {
    try {
      const r = await orderProcessorAPI.getRun(runId);
      setRun(r);
      if (moveToStatusStep) setStep(stepForStatus(r.status));
    } catch (e) { setError(errMsg(e)); }
  }, [runId]);

  useEffect(() => { load(true); }, [load]);

  if (!run) return error ? <Alert severity="error">{error}</Alert> : <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const editable = ['draft', 'reviewed', 'distributors_assigned'].includes(run.status);
  const maxStep = stepForStatus(run.status);

  const cancelRun = async () => {
    if (!window.confirm(`Cancel ${run.run_number}? It stays in history but can no longer be ordered.`)) return;
    try { await orderProcessorAPI.setRunStatus(run.id, 'cancelled'); await load(true); } catch (e) { setError(errMsg(e)); }
  };

  const toDistributors = async () => {
    try {
      if (run.status === 'draft') await orderProcessorAPI.setRunStatus(run.id, 'reviewed');
      await load();
      setStep(1);
    } catch (e) { setError(errMsg(e)); }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Button startIcon={<ArrowBack />} onClick={onBack}>Back</Button>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">
              {run.run_number} · {run.store_name} <StatusChip status={run.status} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {run.source === 'commitment' ? 'Customer commitment orders' : 'Order Optimizer'} · {run.run_date} · by {run.created_by} ·
              {' '}{run.total_items} items analysed · {run.suggested_items} to order · updated {fmtDateTime(run.updated_at)}
            </Typography>
          </Box>
          {editable && <Button color="error" startIcon={<Cancel />} onClick={cancelRun}>Cancel run</Button>}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {run.status === 'cancelled' && <Alert severity="warning" sx={{ mb: 2 }}>This run was cancelled — read-only.</Alert>}

      <Stepper nonLinear activeStep={step} sx={{ mb: 3 }}>
        {STEPS.map((label, idx) => (
          <Step key={label} completed={idx < maxStep}>
            <StepButton onClick={() => setStep(idx)} disabled={idx > maxStep}>{label}</StepButton>
          </Step>
        ))}
      </Stepper>

      {step === 0 && <ReviewStep run={run} editable={editable} onChanged={() => load()} onNext={toDistributors} />}
      {step === 1 && (editable
        ? <DistributorStep run={run} onChanged={() => load()} onGenerated={() => { load(); setStep(2); }} />
        : <Alert severity="info">Distributors are fixed once POs are generated — see the purchase orders.</Alert>)}
      {step === 2 && <PurchaseOrders runId={run.id} />}
    </Box>
  );
};

export default RunWorkspace;
